import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Check, Zap, Loader2, Sparkles, Clock, AlertCircle, FileText, Download } from "lucide-react";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { PLANS, calculateYearlySavings, formatLimit, type PlanType, type BillingPeriod } from "@/lib/stripe";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const Subscription = () => {
  const {
    subscribed,
    plan: currentPlan,
    planName,
    status,
    periodEnd,
    cancelAtPeriodEnd,
    isTrialing,
    trialEnd,
    limits,
    usage,
    loading,
    openCheckout,
    openCustomerPortal,
    refreshSubscription,
  } = useSubscription();

  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  // Factures
  interface InvoiceItem {
    id: string;
    number: string | null;
    date: number;
    amount_paid: number;
    currency: string;
    hosted_invoice_url: string | null;
    invoice_pdf: string | null;
  }
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // Gerer le retour de Stripe Checkout
  useEffect(() => {
    if (searchParams.get("success") === "true") {
      toast({
        title: "Paiement reussi !",
        description: "Votre abonnement a ete active. Bienvenue dans B8ild !",
      });
      refreshSubscription();
    } else if (searchParams.get("canceled") === "true") {
      toast({
        title: "Paiement annule",
        description: "Vous pouvez reprendre a tout moment.",
        variant: "destructive",
      });
    }
  }, [searchParams]);

  // Charger les factures si abonne
  useEffect(() => {
    if (!subscribed) return;
    const fetchInvoices = async () => {
      try {
        setLoadingInvoices(true);
        const { data, error } = await supabase.functions.invoke('list-invoices');
        if (error) throw error;
        if (data?.invoices) {
          setInvoices(data.invoices);
        }
      } catch (err) {
        console.error('Erreur chargement factures:', err);
      } finally {
        setLoadingInvoices(false);
      }
    };
    fetchInvoices();
  }, [subscribed]);

  const handleSubscribe = async (targetPlan: PlanType) => {
    if (targetPlan === "free") return;
    setLoadingPlan(targetPlan);
    try {
      await openCheckout(targetPlan as Exclude<PlanType, "free">, billingPeriod);
    } finally {
      setLoadingPlan(null);
    }
  };

  // Formater la date de fin
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Formater un montant depuis les centimes Stripe
  const formatAmount = (cents: number, currency: string) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(cents / 100);

  // Formater un timestamp Unix en date lisible
  const formatInvoiceDate = (timestamp: number) =>
    new Date(timestamp * 1000).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

  // Plans a afficher (sans free dans la grille principale)
  const displayPlans = PLANS.filter(p => p.id !== "free");

  return (
    <div className="space-y-8 animate-fade-up">
      <div>
        <h1 className="text-4xl font-black text-gradient-primary flex items-center gap-3">
          <CreditCard className="h-9 w-9 text-primary" />
          Mon Abonnement
        </h1>
        <p className="text-muted-foreground mt-2 text-lg">
          Gerez votre abonnement et votre facturation
        </p>
      </div>

      {/* Statut actuel */}
      <Card className="card-premium">
        <CardHeader>
          <CardTitle className="text-2xl font-black">Statut actuel</CardTitle>
          <CardDescription className="text-base">Votre abonnement en cours</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-black">Plan {planName}</p>
                  {isTrialing && trialEnd && (
                    <p className="text-base text-amber-500 mt-1 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Periode d'essai jusqu'au {formatDate(trialEnd)}
                    </p>
                  )}
                  {cancelAtPeriodEnd && periodEnd && (
                    <p className="text-base text-red-500 mt-1 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Annule - Acces jusqu'au {formatDate(periodEnd)}
                    </p>
                  )}
                  {subscribed && !cancelAtPeriodEnd && periodEnd && (
                    <p className="text-base text-muted-foreground mt-1">
                      Prochain renouvellement le {formatDate(periodEnd)}
                    </p>
                  )}
                </div>
                <Badge
                  variant={subscribed ? "default" : "secondary"}
                  className={cn(
                    "text-base font-bold px-4 py-2",
                    isTrialing && "bg-amber-500 hover:bg-amber-600",
                    cancelAtPeriodEnd && "bg-red-500 hover:bg-red-600"
                  )}
                >
                  {isTrialing ? "Essai" : cancelAtPeriodEnd ? "Annule" : subscribed ? "Actif" : "Gratuit"}
                </Badge>
              </div>

              {/* Usage actuel */}
              {currentPlan !== "free" && (
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <p className="text-sm text-muted-foreground">Analyses IA ce mois</p>
                    <p className="text-lg font-bold">
                      {usage.analyses_ia_used} / {limits.max_analyses_ia === -1 ? "Illimite" : limits.max_analyses_ia}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Imports OCR ce mois</p>
                    <p className="text-lg font-bold">
                      {usage.ocr_used} / {limits.max_ocr === -1 ? "Illimite" : limits.max_ocr}
                    </p>
                  </div>
                </div>
              )}

              {subscribed && (
                <Button variant="outline" className="w-full font-bold" size="lg" onClick={openCustomerPortal}>
                  <CreditCard className="mr-2 h-5 w-5" />
                  Gerer mon abonnement
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Toggle Mensuel / Annuel */}
      <div className="flex flex-col items-center gap-4">
        <h2 className="text-3xl font-black">Choisissez votre plan</h2>
        <div className="flex items-center gap-4 bg-muted/50 rounded-full p-1.5">
          <button
            onClick={() => setBillingPeriod("monthly")}
            className={cn(
              "px-6 py-2.5 rounded-full font-bold transition-all",
              billingPeriod === "monthly"
                ? "bg-primary text-primary-foreground shadow-lg"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Mensuel
          </button>
          <button
            onClick={() => setBillingPeriod("yearly")}
            className={cn(
              "px-6 py-2.5 rounded-full font-bold transition-all flex items-center gap-2",
              billingPeriod === "yearly"
                ? "bg-primary text-primary-foreground shadow-lg"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Annuel
            <Badge className="bg-green-500 text-white text-xs">-20%</Badge>
          </button>
        </div>
      </div>

      {/* Plans disponibles */}
      <div className="grid gap-6 lg:grid-cols-3">
        {displayPlans.map((plan, index) => {
          const price = billingPeriod === "monthly" ? plan.pricing.monthly : plan.pricing.yearly;
          const monthlyEquivalent = plan.pricing.monthlyEquivalent;
          const savings = calculateYearlySavings(plan.id as Exclude<PlanType, "free">);
          const isCurrentPlan = plan.id === currentPlan;
          const isLoading = loadingPlan === plan.id;

          return (
            <Card
              key={plan.id}
              className={cn(
                "card-premium relative animate-fade-up",
                plan.popular && "border-accent shadow-xl shadow-accent/20 scale-[1.02]",
                isCurrentPlan && "ring-2 ring-primary"
              )}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <CardHeader>
                {plan.popular && (
                  <Badge className="w-fit mb-3 bg-gradient-accent text-primary font-bold px-3 py-1.5 absolute -top-3 left-1/2 -translate-x-1/2">
                    <Zap className="h-4 w-4 mr-1" />
                    Populaire
                  </Badge>
                )}
                <CardTitle className="text-2xl font-black">{plan.name}</CardTitle>
                <CardDescription className="text-base">{plan.description}</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-black text-gradient-primary">
                    {price}€ HT
                  </span>
                  <span className="text-muted-foreground">
                    {billingPeriod === "monthly" ? " /mois" : " /an"}
                  </span>
                  {billingPeriod === "yearly" && (
                    <p className="text-sm text-green-500 font-medium mt-1">
                      <Sparkles className="inline h-4 w-4 mr-1" />
                      Soit {monthlyEquivalent}€ HT/mois (-{savings}%)
                    </p>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Limites du plan */}
                <div className="pt-4 border-t space-y-2 text-sm text-muted-foreground">
                  <div className="flex justify-between">
                    <span>Chantiers</span>
                    <span className="font-medium text-foreground">{formatLimit(plan.limits.max_chantiers)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Analyses IA/mois</span>
                    <span className="font-medium text-foreground">{formatLimit(plan.limits.max_analyses_ia)}</span>
                  </div>
                </div>

                <Button
                  className={cn(
                    "w-full font-bold",
                    plan.popular && "hover-glow"
                  )}
                  variant={plan.popular ? "default" : "outline"}
                  size="lg"
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={loading || isLoading || isCurrentPlan}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Redirection...
                    </>
                  ) : isCurrentPlan ? (
                    "Plan actuel"
                  ) : (
                    "Choisir ce plan"
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Essai gratuit */}
      <Card className="card-premium bg-gradient-to-r from-primary/10 to-accent/10 border-primary/30">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold">14 jours d'essai gratuit</h3>
              <p className="text-muted-foreground">
                Testez toutes les fonctionnalites sans engagement. Aucune carte requise pour l'essai.
              </p>
            </div>
            <Badge variant="outline" className="text-primary border-primary px-4 py-2 text-base">
              <Clock className="mr-2 h-4 w-4" />
              14 jours gratuits
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Methode de paiement */}
      <Card className="card-premium">
        <CardHeader>
          <CardTitle className="text-2xl font-black">Methode de paiement</CardTitle>
          <CardDescription className="text-base">
            Gerez vos informations de paiement
          </CardDescription>
        </CardHeader>
        <CardContent>
          {subscribed ? (
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Gerez vos informations de paiement via le portail client Stripe.
              </p>
              <Button variant="outline" size="lg" className="font-bold" onClick={openCustomerPortal}>
                <CreditCard className="mr-2 h-5 w-5" />
                Gerer mes moyens de paiement
              </Button>
            </div>
          ) : (
            <p className="text-muted-foreground">
              Aucune carte enregistree. Souscrivez a un plan payant pour ajouter une carte.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Mes factures */}
      {subscribed && (
        <Card className="card-premium">
          <CardHeader>
            <CardTitle className="text-2xl font-black flex items-center gap-2">
              <FileText className="h-6 w-6" />
              Mes factures
            </CardTitle>
            <CardDescription className="text-base">
              Vos 12 dernieres factures d'abonnement
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingInvoices ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : invoices.length === 0 ? (
              <p className="text-muted-foreground py-4">
                Aucune facture pour le moment.
              </p>
            ) : (
              <div className="space-y-3">
                {invoices.map((inv) => (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {inv.number || "Facture"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatInvoiceDate(inv.date)}
                      </p>
                    </div>
                    <div className="text-right mr-4">
                      <p className="font-bold">
                        {formatAmount(inv.amount_paid, inv.currency)}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const url = inv.hosted_invoice_url || inv.invoice_pdf;
                        if (url) window.open(url, "_blank");
                      }}
                      disabled={!inv.hosted_invoice_url && !inv.invoice_pdf}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Subscription;
